import React, { useContext } from 'react'
import PropTypes from 'prop-types'
import Loading from '../../molecules/Loading'
import LoadingView from '../../pages/LoadingView'
import { ICON_NAMES } from '../../../constants'
import { IntegrationAuthContext, useIntegrationAuthState } from './IntegrationAuthContext'
import IntegrationAuthGate from './IntegrationAuthGate'

const IntegrationAuthProvider = ({
  integration,
  children,
  allowAuth = true,
  size = 'md',
  loadingType = 'default',
  authLabels = {
    connectButton: 'Connect',
    buttonIcon: ICON_NAMES.link,
    title: 'Authentication Required',
    subtitle: ''
  },
  errorLabels = {
    title: 'Error loading authentication',
    subtitle: 'An error occurred while checking authentication status. Please try again later.'
  },
  authStyles = {},
  authClasses = {},
  width = '100%',
  height = '100%'
}) => {
  const parentAuth = useContext(IntegrationAuthContext)
  const hasAuthenticatedParent = parentAuth?.isAuthenticated && parentAuth?.integration === integration

  const authState = useIntegrationAuthState(integration, {
    enabled: !hasAuthenticatedParent && !!integration
  })

  if (hasAuthenticatedParent) {
    return (
      <IntegrationAuthContext.Provider value={parentAuth}>
        {children}
      </IntegrationAuthContext.Provider>
    )
  }

  if (authState.isLoading) {
    if (loadingType === 'circular') {
      return <Loading />
    }
    return <LoadingView width={width} height={height} />
  }

  return (
    <IntegrationAuthGate
      authState={authState}
      allowAuth={allowAuth}
      size={size}
      authLabels={authLabels}
      errorLabels={errorLabels}
      authStyles={authStyles}
      authClasses={authClasses}
      width={width}
      height={height}
    >
      {children}
    </IntegrationAuthGate>
  )
}

IntegrationAuthProvider.propTypes = {
  integration: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  allowAuth: PropTypes.bool,
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  loadingType: PropTypes.oneOf(['default', 'circular']),
  authLabels: PropTypes.shape({
    connectButton: PropTypes.string,
    buttonIcon: PropTypes.string,
    title: PropTypes.string,
    subtitle: PropTypes.string
  }),
  errorLabels: PropTypes.shape({
    title: PropTypes.string,
    subtitle: PropTypes.string
  }),
  authStyles: PropTypes.shape({
    container: PropTypes.object,
    content: PropTypes.object,
    title: PropTypes.object,
    subtitle: PropTypes.object,
    button: PropTypes.object
  }),
  authClasses: PropTypes.shape({
    container: PropTypes.string,
    content: PropTypes.string,
    title: PropTypes.string,
    subtitle: PropTypes.string,
    button: PropTypes.string
  }),
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
}

export default IntegrationAuthProvider
